import { useMutation, useQueryClient } from "@tanstack/react-query";
import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";

import {
  callFunctionIfExists,
  getErrorMessage,
} from "../../../utils/GeneralUtils";
import { newToastEvent, updateToastEvent } from "../../../utils/ToastUtil";
import { setSchemaAtom, tenantIDAtom } from "../../../atoms/GlobalAtoms";
import { AppConfirm } from "../../../utils/PopupUtil";
import GlobalAppAPI from "../../api/GlobalAppAPI";

export interface InstallAppPayload {
  globalAppID: string;
  workspaceID: string;
  supportTargetCloudID?: string;
}

interface Props {
  payload: InstallAppPayload;
  onSuccess?: () => void;
}

const installGlobalAppIDAtom = atom("");

/**
 * For installing GLOBAL apps
 * Cloud Admins ONLY!
 * Limit one workspace per cloud currently
 */
const useInstallGlobalApp = () => {
  const client = useQueryClient();
  const setSchema = useSetAtom(setSchemaAtom);
  const tenantID = useAtomValue(tenantIDAtom);
  const [installID, setInstallID] = useAtom(installGlobalAppIDAtom);

  const installGlobalApp = useMutation({
    mutationFn: async ({ payload }: Props) => {
      newToastEvent(
        "success",
        "Installing global app...",
        "InstallingGlobalApp",
        {
          loading: true,
          autoClose: false,
        }
      );
      setInstallID(payload.globalAppID);
      return GlobalAppAPI.installGlobalApp(payload);
    },
    onSuccess: async (data, { payload, onSuccess }) => {
      const schema = data?.data?.data?.schema;
      const workspaceIds = data?.data?.data?.workspaceIds || [
        payload.workspaceID,
      ];
      schema && setSchema(schema);

      for (let workspaceID of workspaceIds) {
        await client.refetchQueries({ queryKey: ["workspace", workspaceID] });
        await client.refetchQueries({ queryKey: ["page"] });
        await client.refetchQueries({ queryKey: ["cloudApps", tenantID] });
      }

      AppConfirm({
        title: "Installation Complete",
        icon: "success",
        html: `Successfully installed Global App!`,
        confirmButtonText: "OK",
        confirmButtonColor: "var(--primary)",
      });

      updateToastEvent(
        "success",
        `Successfully installed Global App!`,
        "InstallingGlobalApp",
        {
          loading: false,
          autoClose: true,
        }
      );

      callFunctionIfExists(onSuccess);
    },
    onError: (error) => {
      AppConfirm({
        title: "Error",
        html: `Failed to install app! <br/> ${getErrorMessage(error)}`,
        confirmButtonText: "OK",
      });

      updateToastEvent(
        "error",
        `Failed to install app! ${getErrorMessage(error)}`,
        "InstallingGlobalApp",
        {
          loading: false,
          autoClose: true,
        }
      );
    },
    onSettled: () => setInstallID(""),
  });

  return {
    installGlobalApp: installGlobalApp.mutate,
    installID: installID,
  };
};

export default useInstallGlobalApp;
